import React from 'react';
import Icon from '../../../components/AppIcon';

const ValueProposition = () => {
  const pillars = [
    {
      id: 1,
      icon: "Cpu",
      title: "Build Internal AI Capabilities",
      description: "Develop proprietary AI systems tailored to your unique business needs. No more one-size-fits-all solutions that compromise your competitive advantage.",
      benefits: ["Custom AI models", "Internal expertise", "Full control"]
    },
    {
      id: 2,
      icon: "Unlock",
      title: "Eliminate Vendor Dependencies",
      description: "Break free from expensive licensing, forced upgrades, and feature limitations. Own your AI infrastructure completely.",
      benefits: ["No licensing fees", "Freedom to modify", "Data sovereignty"]
    },
    {
      id: 3,
      icon: "Shield",
      title: "Future-Proof Your Organization",
      description: "Build sustainable AI capabilities that evolve with your business. Avoid the constant cycle of vendor migrations and platform changes.",
      benefits: ["Long-term stability", "Scalable architecture", "Innovation freedom"]
    }
  ];

  return (
    <section className="py-20 lg:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl lg:text-5xl font-bold text-gray-900 mb-6">
            The Three Pillars of AI Independence
          </h2>
          <p className="text-xl text-gray-600 leading-relaxed">
            Transform your organization from AI consumer to AI owner. Build capabilities that compound over time instead of costs that escalate forever.
          </p>
        </div>

        {/* Pillars Grid */}
        <div className="grid lg:grid-cols-3 gap-8 lg:gap-12">
          {pillars?.map((pillar, index) => (
            <div key={pillar?.id} className="relative group">
              {/* Connection Line */}
              {index < pillars?.length - 1 && (
                <div className="hidden lg:block absolute top-16 left-full w-12 h-0.5 bg-gradient-to-r from-gray-300 to-transparent transform translate-x-6"></div>
              )}
              
              <div className="bg-white rounded-2xl p-8 shadow-executive hover:shadow-executive-lg transition-all duration-300 border border-gray-100 group-hover:border-gray-200">
                {/* Icon */}
                <div className="w-16 h-16 bg-gray-900 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  <Icon name={pillar?.icon} size={28} className="text-white" />
                </div>

                {/* Content */}
                <div className="space-y-4">
                  <h3 className="text-2xl font-bold text-gray-900">
                    {pillar?.title}
                  </h3>
                  
                  <p className="text-gray-600 leading-relaxed">
                    {pillar?.description}
                  </p>

                  {/* Benefits List */}
                  <ul className="space-y-2">
                    {pillar?.benefits?.map((benefit, idx) => (
                      <li key={idx} className="flex items-center text-sm text-gray-700">
                        <Icon name="Check" size={16} className="text-green-500 mr-3 flex-shrink-0" />
                        {benefit}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Step Number */}
                <div className="absolute -top-4 -right-4 w-8 h-8 bg-gray-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
                  {index + 1}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-16">
          <div className="inline-flex items-center px-6 py-3 bg-gray-50 rounded-full text-gray-700 font-medium">
            <Icon name="ArrowRight" size={16} className="mr-2" />
            Ready to build your AI independence strategy?
          </div>
        </div>
      </div>
    </section>
  );
};

export default ValueProposition;